import { useState, useEffect, useRef } from 'react'
import { requestMobile } from '@/lib/request'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RefreshCw, Filter, X } from 'lucide-react'
import { toast } from 'sonner'
import { usePersistentState } from '@/hooks/usePersistentState'

interface AddressItem {
  id: string
  name?: string
  address: string
  address_name: string
  user: string
  update_time?: string
}

export default function Addresses() {
  const [data, setData] = useState<AddressItem[]>([])
  const [loading, setLoading] = useState(false)
  const latestRequestRef = useRef(0)
  const latestAppliedRequestRef = useRef(0)

  // 筛选状态
  const [showFilter, setShowFilter] = useState(false)
  const [filters, setFilters, clearFilters] = usePersistentState<Record<string, any>>(
    'mobile:addresses:filters',
    {},
  )

  const fetchData = async () => {
    const requestId = ++latestRequestRef.current
    setLoading(true)
    try {
      const resp = await requestMobile('/system/api/query/addresses', {
        page_num: 1,
        page_size: 100,
        query_filter: filters,
      })
      if (
        requestId > latestAppliedRequestRef.current &&
        resp &&
        !resp.code
      ) {
        latestAppliedRequestRef.current = requestId
        setData(resp.data.list)
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error)
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  const handleRefresh = () => {
    fetchData()
  }

  const handleResetFilters = () => {
    clearFilters()
  }

  const getActiveFilterCount = () => {
    return Object.keys(filters).length
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('复制成功')
    } catch (error) {
      console.error('Failed to copy:', error)
      toast.error('复制失败')
    }
  }

  return (
    <div className="p-4 max-w-2xl mx-auto min-h-full">
      {/* 头部 */}
      <div className="flex justify-between items-center mb-4" style={{ paddingTop: 'max(env(safe-area-inset-top), 8px)' }}>
        <h1 className="text-2xl font-bold">地址管理</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilter(!showFilter)}
            className="relative"
          >
            <Filter className="h-4 w-4" />
            {getActiveFilterCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {getActiveFilterCount()}
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* 筛选面板 */}
      {showFilter && (
        <div className="mb-4 p-4 border rounded-lg space-y-3 bg-muted/30">
          {/* 地址筛选 */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">地址</Label>
            <Input
              placeholder="address"
              value={filters.address || ''}
              onChange={(e) => {
                const newFilters = { ...filters }
                if (e.target.value) {
                  newFilters.address = e.target.value
                } else {
                  delete newFilters.address
                }
                setFilters(newFilters)
              }}
            />
          </div>

          {/* 绑定账号筛选 */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">绑定账号</Label>
            <Input
              placeholder="name"
              value={filters.name || ''}
              onChange={(e) => {
                const newFilters = { ...filters }
                if (e.target.value) {
                  newFilters.name = e.target.value
                } else {
                  delete newFilters.name
                }
                setFilters(newFilters)
              }}
            />
          </div>

          {/* 地址名称筛选 */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">地址名称</Label>
            <Input
              placeholder="address_name"
              value={filters.address_name || ''}
              onChange={(e) => {
                const newFilters = { ...filters }
                if (e.target.value) {
                  newFilters.address_name = e.target.value
                } else {
                  delete newFilters.address_name
                }
                setFilters(newFilters)
              }}
            />
          </div>

          {/* 操作按钮 */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={handleResetFilters}
              disabled={getActiveFilterCount() === 0}
            >
              <X className="h-3 w-3 mr-1" />
              重置
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={() => setShowFilter(false)}
            >
              完成
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {data.map((item) => (
          <Card key={item.id} className="p-4">
            <div className="space-y-2">
              <div className="font-medium">{item.name || '未绑定'}</div>
              <div className="flex justify-between items-center text-sm text-muted-foreground">
                <div>{item.address_name}</div>
                <div>{item.user}</div>
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm break-all flex-1 mr-2">{item.address}</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyToClipboard(item.address)}
                >
                  复制
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {data.length === 0 && !loading && (
        <div className="mt-8 text-center text-muted-foreground">暂无地址</div>
      )}
    </div>
  )
}
